import { FC } from 'react'
import { Currency } from '@sequencehq/api'
import { CreditNoteApplicationStatus, LineItem } from 'CreditNotes/types'
import { toMoney } from '@sequencehq/utils'
import {
  Text,
  Card,
  GreyGrey10,
  GreyGrey70,
  GreyGrey80,
  GreyGrey90,
  Primary13SemiBold,
  Primary13Regular,
  Primary20SemiBold,
  Td,
  TextOverflow,
  Totals,
  Tr,
  Box,
  Flex
} from '@sequencehq/core-components'
import { getTaxPercentage } from 'CreditNotes/utils/totals'

type Props = {
  lineItems: LineItem[]
  currency: Currency
  netTotal: string
  totalTax: string
  grossTotal: string
  /** Credit still available to apply. Omitted when settlement isn't in play. */
  balance?: string
  applicationStatus?: CreditNoteApplicationStatus
}

export const CreditNoteAdjustmentTotal: FC<Props> = ({
  lineItems,
  currency,
  netTotal,
  totalTax,
  grossTotal,
  balance,
  applicationStatus
}) => {
  const taxPercentage = getTaxPercentage(lineItems)
  // Unapplied, so a remaining row would only repeat the credit total.
  const showsCreditRemaining =
    balance !== undefined && applicationStatus !== 'UNAPPLIED'

  return (
    <Card variant="v2" padding="12px 16px" data-testid="creditNote.totalsCard">
      <Flex gap={2} direction="column">
        <Text {...Primary13SemiBold} color={GreyGrey80}>
          Credit total
        </Text>
        <Box {...Primary20SemiBold} color={GreyGrey90}>
          {toMoney({ currency, value: grossTotal })}
        </Box>

        <Box
          background={GreyGrey10}
          px={2}
          py={1}
          borderRadius={6}
          mx={-2}
          mt={2}
        >
          <Totals currency={currency}>
            {lineItems.map(lineItem => (
              <LineItemRow
                key={lineItem.id}
                title={lineItem.title}
                amount={lineItem.netTotal}
                currency={currency}
              />
            ))}
            {!!lineItems.length && <Totals.Divider />}
            <Totals.Row title="Credit subtotal" amount={netTotal} />
            {/* Pinned test id: the title moves with the line items' tax rates. */}
            <Totals.Row
              title={taxPercentage ? `Tax (${taxPercentage}%)` : 'Tax'}
              amount={totalTax}
              testId="Totals.Tax"
            />
            <Totals.Divider />
            {/* Only the closing row is emphasised. */}
            {showsCreditRemaining ? (
              <Totals.Row title="Credit total" amount={grossTotal} />
            ) : (
              <Totals.Total title="Credit total" amount={grossTotal} />
            )}
            {/* Applied credit rows belong here, once the read model returns
                the applications (IE-3398). */}
            {showsCreditRemaining && (
              <>
                <Totals.Divider />
                <Totals.Total
                  title="Credit remaining"
                  amount={balance}
                  testId="creditNote.creditRemaining"
                />
              </>
            )}
          </Totals>
        </Box>
      </Flex>
    </Card>
  )
}

const LineItemRow: FC<{
  title: string
  amount: string
  currency: Currency
}> = ({ title, amount, currency }) => {
  return (
    <Tr>
      <Td verticalAlign="top" maxW="0" width="100%">
        <Box {...Primary13Regular} color={GreyGrey70}>
          <TextOverflow>{title}</TextOverflow>
        </Box>
      </Td>
      <Td verticalAlign="top" textAlign="right" width="1px">
        <Text
          {...Primary13Regular}
          color={GreyGrey70}
          textAlign="right"
          whiteSpace="nowrap"
        >
          {toMoney({ currency, value: amount })}
        </Text>
      </Td>
    </Tr>
  )
}
